import React, { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import logo from '../../images/shiphawk-logo-navy-retina.png';
import SignInFlow from '../SignIn/components/SignInFlow.js';
import AuthorizationProgress from './components/AuthorizationProgress.js';
import PermissionsStep from './components/PermissionsStep.js';
import { approve, deny, fetchAuthorizationSession } from './actions.js';
import styles from './styles.module.css';

const DEFAULT_ERROR = 'Something went wrong. Please try again.';

const errorMessage = (error) => error.response?.data?.error || DEFAULT_ERROR;

function OAuthAuthorize() {
  const { authorization_key: authorizationKey } = useParams();
  const location = useLocation();
  const [step, setStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [authorization, setAuthorization] = useState(null);

  useEffect(() => {
    document.title = 'Authorize access | ShipHawk';

    const resumeAuthorization = async () => {
      try {
        const { data } = await fetchAuthorizationSession({ authorizationKey });
        if (data?.id) {
          setAuthorization(data);
          setStep(1);
        }
      } catch (requestError) {
        if (requestError.response?.status !== 404) {
          console.error(requestError);
        }
      }
    };

    resumeAuthorization();
  }, [authorizationKey]);

  useEffect(() => {
    const callbackError = new URLSearchParams(location.search).get('error');
    if (callbackError) {
      setError(callbackError);
    }
  }, [location.search]);

  const onAuthenticated = (data) => {
    setError('');
    setAuthorization(data);
    setStep(1);
  };

  const onApprove = async (scopes) => {
    setIsSubmitting(true);
    setError('');

    try {
      const { data } = await approve({
        authorizationKey,
        scopes,
      });
      window.location.replace(data.redirect_url);
    } catch (requestError) {
      setError(errorMessage(requestError));
      setIsSubmitting(false);
    }
  };

  const onDeny = async () => {
    setIsSubmitting(true);
    setError('');

    try {
      const { data } = await deny({
        authorizationKey,
      });
      window.location.replace(data.redirect_url);
    } catch (requestError) {
      setError(errorMessage(requestError));
      setIsSubmitting(false);
    }
  };

  return (
    <main className={styles.page}>
      <div className={styles.content}>
        <AuthorizationProgress step={step} />
        {step === 0 && (
          <section className={styles.card} data-testid="oauth-sign-in">
            <img className={styles.logo} src={logo} alt="ShipHawk" />
            <div className={styles.signInFlow}>
              <SignInFlow
                authorizationKey={authorizationKey}
                emailDescription="Third Party Integration is requesting access to your account"
                initialError={error}
                onAuthenticated={onAuthenticated}
                showForgotPassword={false}
                showTrustDevice={false}
                title="Sign in to ShipHawk"
              />
            </div>
            <p className={styles.trustHint}>
              By signing in you confirm this is a trusted application.
            </p>
          </section>
        )}
        {step === 1 && authorization && (
          <PermissionsStep
            authorization={authorization}
            error={error}
            isSubmitting={isSubmitting}
            onApprove={onApprove}
            onDeny={onDeny}
          />
        )}
        <p className={styles.secured}>
          <span aria-hidden="true">🔒</span>
          Secured by ShipHawk · Your credentials are never shared with third
          parties
        </p>
      </div>
    </main>
  );
}

export default OAuthAuthorize;
